import { useTranslation } from '@kgc/i18n';

import { useDefaultWarehouse } from './use-default-warehouse';

/**
 * Q-T167 — a raktár-feloldás EGYETLEN igazság-forrása a napi zárás/nyitás wizardokhoz.
 *
 * ⚠️ Miért kell helper egy 4-ágú ternaryhoz? Mert a leképezés **négy** helyre szóródott
 * szét, **két különböző** szabállyal ([CR] Botka F2):
 *   DailyCloseWizard.handleSubmit  → 4-ágú (loading / empty / error / unresolved)
 *   DailyOpenWizard.handleSubmit   → ugyanaz
 *   DailyCloseWizard figyelmeztető sáv → csak 2-ágú (empty / error)
 *   DailyOpenWizard figyelmeztető sáv  → ugyanaz
 *
 * A divergencia egy VALÓS rést szült ([CR] Botka F1): a `disabled` mind a négy állapotra
 * tiltott, a magyarázó sáv viszont csak kettőt fedett → az `unresolved` ágon a user egy
 * **letiltott gombot nézett magyarázat nélkül**. Ez a false-affordance TÜKÖRKÉPE:
 * ott „kattintható, de nem sikerülhet", itt „nem kattintható, és nem mondjuk meg miért".
 * Mindkettő ugyanaz a család: a néma hiány veszélyesebb, mint a hangos hiba.
 *
 * A helper garanciája: `blockMessage` PONTOSAN akkor nem-null, ha `isBlocked` — tehát
 * néma tiltás szerkezetileg nem tud előállni. Ha új blokkoló állapot jön, itt egy helyen
 * kell bővíteni, és minden fogyasztó (gomb + sáv + submit-guard) automatikusan követi.
 *
 * A helpert 🤖 Botka írta a #1021 [CR]-jében; itt a fogyasztókkal együtt vezetjük be.
 */
export interface WarehouseGate {
  /** A feloldott raktár id-ja, vagy null ha blokkolt. */
  warehouseId: string | null;
  /** Blokkolt-e a művelet (nincs használható raktár). */
  isBlocked: boolean;
  /** A blokkolás OKA, a usernek szólóan. Pontosan akkor nem null, ha `isBlocked`. */
  blockMessage: string | null;
  /** Újrapróbálkozás (tranziens hálózati hiba után). */
  refetch: () => void;
}

export function useWarehouseGate(): WarehouseGate {
  const { t } = useTranslation();
  const { warehouseId, isLoading, isEmpty, isError, refetch } = useDefaultWarehouse();

  if (warehouseId) {
    return { warehouseId, isBlocked: false, blockMessage: null, refetch };
  }

  const blockMessage = isLoading
    ? t('admin.warehouseLoading')
    : isEmpty
      ? t('admin.warehouseMissing')
      : isError
        ? t('admin.warehouseFetchFailed')
        : t('admin.warehouseUnresolved');

  return { warehouseId: null, isBlocked: true, blockMessage, refetch };
}
